import React, {Fragment, useMemo} from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {PopoverHeading} from 'app/general/overlays';
import StateButton from 'app/general/widgets/StateButton';
import {useArticles, useBulkDeleteArticles} from 'app/kbs/api';
import {gt} from 'app/utils/i18n';

import {
  KBArticleSerializedType,
  KBCategorySerializedType,
  KnowledgebaseSerializedType,
} from 'sweet-types';

interface Props {
  onSuccess: () => Promise<any>;
  documentUrls: Array<string>;
}

interface GroupedArticles {
  id: number;
  category: KBCategorySerializedType;
  knowledgebase: KnowledgebaseSerializedType;
  articles: Array<KBArticleSerializedType>;
}

export const KBSelectedArticleList = ({onSuccess, documentUrls}: Props) => {
  const {data: articleData} = useArticles({
    document_url: documentUrls.join(','),
  });

  const [bulkDeleteArticles] = useBulkDeleteArticles();

  const articlesByKbCategory: Array<GroupedArticles> = useMemo(() => {
    return Object.values(
      articleData?.results.reduce((acc, article) => {
        const categoryId = article.category.id;

        if (!acc[categoryId]) {
          acc[categoryId] = {
            id: article.id,
            category: article.category,
            knowledgebase: article.knowledgebase,
            articles: [],
          };
        }

        acc[categoryId].articles.push(article);

        return acc;
      }, {}) ?? {}
    );
  }, [articleData?.results]);

  function doRemoveArticles(
    articles: Array<KBArticleSerializedType>
  ): Promise<unknown> {
    return bulkDeleteArticles({
      articles: articles.map((article) => article.url),
    })
      .unwrap()
      .then(onSuccess);
  }

  return articlesByKbCategory?.length > 0 ? (
    <Fragment>
      <PopoverHeading className="popover-header">
        {gt('Knowledge Bases')}
      </PopoverHeading>
      <ul
        className="list-group list-group-flush flex-grow-1 flex-shrink-0 overflow-auto"
        style={{
          maxHeight: 'calc(60vh - 230px)',
        }}
      >
        {articlesByKbCategory?.map((article) => (
          <li
            key={article.id}
            style={{
              borderColor: 'rgba(255,255,255,0.5)',
            }}
            className={cs(
              'active list-group-item py-1 d-flex align-items-center ' +
                'justify-content-between pr-0'
            )}
          >
            <div className="text-truncate">
              <span className="d-block text-truncate">
                {article.category.name}
              </span>
              <small className="block text-truncate">
                {article.knowledgebase.name}
              </small>
            </div>
            <LazyTooltip
              placement="left"
              tooltip={gt(`Remove from this category`)}
            >
              <StateButton
                className="btn-sm btn-link py-1 px-3 text-white"
                onClick={() => doRemoveArticles(article.articles)}
              >
                <i className="fa fa-fw fa-times" />
              </StateButton>
            </LazyTooltip>
          </li>
        ))}
      </ul>
    </Fragment>
  ) : null;
};
